import React from "react";
import { useTranslation } from "react-i18next";
import { useFeatureEntitlements } from "@frontegg/react";
import { Button } from "@mui/material";
import { Drawer } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import EmployeeSearchAutoComplete from "../../common/EmployeeSearchAutoComplete";
import CardSearchAutoComplete from "components/admin/cards/CardSearchAutoComplete";
import CategorySearchAutoComplete from "../../common/CategorySearchAutoComplete";
import AllocationSearchAutoComplete from "components/common/AllocationSearchAutoComplete";
import { AnalyticsFilters, clearDrawerFilters } from "./analyticsFilters";

interface FiltersDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: AnalyticsFilters;
  onChange: (next: AnalyticsFilters) => void;
}

/**
 * The filters that need a server search to pick from. The account and the
 * timeframe are short fixed lists, so they live on the page's filter bar.
 */
const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
  open,
  onClose,
  filters,
  onChange,
}) => {
  const { t } = useTranslation();
  const { isEntitled: featureAllocations } = useFeatureEntitlements(
    "feature_allocations",
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t("Set Filters")}
      footer={
        <Inline justify="between" align="center" sx={{ width: "100%" }}>
          <Button
            variant="default-outlined"
            onClick={() => onChange(clearDrawerFilters(filters))}
          >
            {t("Clear Filters")}
          </Button>
          <Button variant="primary-filled" onClick={onClose}>
            {t("Exit")}
          </Button>
        </Inline>
      }
    >
      {t("Filter transactions for analytics")}

      <Stack gap="var(--space-6)">
        <CategorySearchAutoComplete
          title={t("Category")}
          expenseCategory={filters.expenseCategory}
          onSelectExpenseCategory={(expenseCategory) =>
            onChange({ ...filters, expenseCategory })
          }
        />

        <EmployeeSearchAutoComplete
          title={t("Purchaser")}
          employee={filters.employee}
          handleSelectEmployee={(employee) =>
            onChange({ ...filters, employee })
          }
        />

        <CardSearchAutoComplete
          title={t("Card")}
          card={filters.card}
          onSelectCard={(card) => onChange({ ...filters, card })}
        />

        {featureAllocations && (
          <AllocationSearchAutoComplete
            title={t("Allocation")}
            allocation={filters.allocation}
            allocationType={filters.allocationType || undefined}
            onSelectAllocation={(allocation) =>
              onChange({
                ...filters,
                allocation,
                allocationType: allocation?.allocationType || null,
              })
            }
            showAll
          />
        )}
      </Stack>
    </Drawer>
  );
};

export default FiltersDrawer;
